import { z } from "zod";
import { ExtraCategory } from "../ExtraCategory.enum";
import { CategoryLimit } from "./CategoryLimit";
import { ValidationIssue } from "./ValidationIssue";

const CategoryLimitsSchema = z.object({
  featureActive: z.boolean(),
  sectionVisible: z.boolean(),
  blocks: z
    .array(z.custom<CategoryLimit>(value => value instanceof CategoryLimit))
    .default([])
    .refine(blocks => new Set(blocks.map(block => block.category)).size === blocks.length, {
      message: "Duplicate category block",
    }),
});

export type CategoryLimitsProps = z.input<typeof CategoryLimitsSchema>;

/** The *Limits per category* section of one accommodation, one block per category. */
export class CategoryLimits {
  public readonly featureActive: boolean;
  public readonly blocks: readonly CategoryLimit[];
  private readonly sectionVisible: boolean;

  private constructor(props: z.infer<typeof CategoryLimitsSchema>) {
    this.featureActive = props.featureActive;
    this.sectionVisible = props.sectionVisible;
    this.blocks = Object.freeze([...props.blocks]);
  }

  static of(props: CategoryLimitsProps): CategoryLimits {
    return new CategoryLimits(CategoryLimitsSchema.parse(props));
  }

  /** The section is rendered only when the backend says so. */
  isSectionVisible(): boolean {
    return this.sectionVisible && this.blocks.length > 0;
  }

  /** In the order received: Vehicle, then Facilities. */
  categories(): readonly ExtraCategory[] {
    return this.blocks.map(block => block.category);
  }

  block(category: ExtraCategory): CategoryLimit | undefined {
    return this.blocks.find(block => block.category === category);
  }

  /** Replaces the block of the same category; a category the section lacks is refused. */
  withBlock(limit: CategoryLimit): CategoryLimits {
    if (!this.block(limit.category)) {
      throw new Error(`No ${limit.category} block in this section`);
    }
    return CategoryLimits.of({
      featureActive: this.featureActive,
      sectionVisible: this.sectionVisible,
      blocks: this.blocks.map(block => (block.category === limit.category ? limit : block)),
    });
  }

  /** Pre-checks every block that is on, each one on its own. */
  preCheck(): ValidationIssue[] {
    return this.blocks.flatMap(block => block.preCheck());
  }
}
